/**
 * Dexasign Verify Widget
 * Auto-initializing verification widget similar to reCAPTCHA
 */

import { VerifyModal } from './VerifyModal.js';
import '../styles.css';

class DexasignVerifyWidget {
    constructor() {
        this.modals = new Map(); // Track multiple widget instances
        this.autoInitialized = false;
    }

    /**
     * Render a verification widget
     * @param {string|HTMLElement} container - DOM element or selector
     * @param {object} options - Configuration options
     */
    render(container, options = {}) {
        const element = typeof container === 'string' 
            ? document.querySelector(container) 
            : container;

        if (!element) {
            console.error('DexasignVerify: Container not found');
            return null;
        }

        const config = {
            siteKey: options.siteKey || element.dataset?.sitekey,
            applicationId: options.applicationId || element.dataset?.applicationId,
            apiUrl: options.apiUrl,
            onComplete: options.onComplete,
            onCancel: options.onCancel,
            onError: options.onError,
            mode: options.mode || 'popup' // 'popup' or 'inline'
        };

        if (!config.applicationId) {
            console.error('DexasignVerify: application_id is required');
            return null;
        }

        // Create modal instance
        const modal = new VerifyModal(config);

        // Store instance
        const id = this.generateId();
        this.modals.set(id, modal);

        // Render inline button or setup for popup
        if (config.mode === 'popup') {
            this.renderButton(element, modal, config);
        } else {
            // Inline mode: show directly in container
            modal.createModal();
            element.appendChild(modal.modal);
            modal.modal.style.position = 'relative';
            modal.modal.style.transform = 'none';
            modal.modal.style.top = 'auto';
            modal.modal.style.left = 'auto';
            modal.show();
        }

        return {
            id,
            show: () => modal.show(),
            hide: () => modal.hide(),
            destroy: () => {
                modal.destroy();
                this.modals.delete(id);
            }
        };
    }

    /**
     * Render verification checkbox for popup mode (reCAPTCHA-style)
     */
    renderButton(container, modal, config) {
        // Create checkbox UI
        container.innerHTML = `
            <div class="dexasign-verify-container" style="
                display: flex;
                align-items: center;
                gap: 16px;
                padding: 20px;
                background: white;
                border-radius: 8px;
                border: 1px solid #D1D5DB;
                cursor: pointer;
                transition: border-color 0.2s;
                user-select: none;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            ">
                <div class="dexasign-checkbox" style="
                    width: 24px;
                    height: 24px;
                    border: 2px solid #D1D5DB;
                    border-radius: 4px;
                    transition: all 0.2s;
                    flex-shrink: 0;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                "></div>
                <span class="dexasign-label" style="
                    font-size: 14px;
                    font-weight: 400;
                    color: #374151;
                    flex: 1;
                ">I am a human</span>
                <img src="/static/Dexasign-Logo.svg" alt="Dexasign" style="
                    width: 28px;
                    height: 28px;
                    flex-shrink: 0;
                " onerror="this.style.display='none'">
            </div>
        `;

        const containerDiv = container.querySelector('.dexasign-verify-container');
        const checkbox = container.querySelector('.dexasign-checkbox');
        
        let isVerified = false;

        // Hover effect
        containerDiv.addEventListener('mouseenter', () => {
            if (!isVerified) {
                checkbox.style.borderColor = '#667eea';
            }
        });
        
        containerDiv.addEventListener('mouseleave', () => {
            if (!isVerified) {
                checkbox.style.borderColor = '#D1D5DB';
            }
        });

        // Click handler
        containerDiv.addEventListener('click', () => {
            if (isVerified) return;

            containerDiv.style.pointerEvents = 'none';
            containerDiv.style.opacity = '0.7';
            modal.show();
            
            // Handle completion
            const originalOnComplete = config.onComplete;
            const originalOnCancel = config.onCancel;
            
            modal.onComplete = (result) => {
                isVerified = true;
                containerDiv.style.pointerEvents = 'auto';
                containerDiv.style.opacity = '1';
                containerDiv.style.cursor = 'default';
                
                // Update checkbox with checkmark
                checkbox.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                checkbox.style.borderColor = '#667eea';
                checkbox.innerHTML = `
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4L6 11.5L2.5 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                `;
                
                if (originalOnComplete) originalOnComplete(result);
            };
            
            modal.onCancel = () => {
                containerDiv.style.pointerEvents = 'auto';
                containerDiv.style.opacity = '1';
                if (originalOnCancel) originalOnCancel();
            };
        });
    }

    /**
     * Auto-initialize widgets from data attributes
     * Called on DOM ready
     */
    autoInit() {
        if (this.autoInitialized) return;
        
        const elements = document.querySelectorAll('.dexasign-verify');
        
        elements.forEach(element => {
            const siteKey = element.dataset.sitekey;
            const callback = element.dataset.callback;
            const mode = element.dataset.mode || 'popup';

            const options = {
                siteKey,
                mode,
                onComplete: callback ? window[callback] : undefined
            };

            this.render(element, options);
        });

        this.autoInitialized = true;
    }

    /**
     * Generate unique ID
     */
    generateId() {
        return `dv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Get widget instance by ID
     */
    getInstance(id) {
        return this.modals.get(id);
    }

    /**
     * Promise-based verification — wraps render() for imperative use.
     * The developer stays in control of their own code flow:
     *
     *   const result = await DexasignVerify.verifyPresence({ applicationId: '...' });
     *   if (!result.ok) return showError();
     *   form.submit();
     *
     * @param {object} options
     * @param {string} options.applicationId
     * @param {string} [options.apiUrl]
     * @returns {Promise<{ok: boolean, token?: string}>}
     */
    verifyPresence(options = {}) {
        return new Promise((resolve, reject) => {
            const tempDiv = document.createElement('div');
            document.body.appendChild(tempDiv);

            const cleanup = () => {
                if (tempDiv.parentNode) tempDiv.parentNode.removeChild(tempDiv);
            };

            const widget = this.render(tempDiv, {
                applicationId: options.applicationId,
                apiUrl: options.apiUrl,
                mode: 'popup',
                onComplete: (result) => {
                    cleanup();
                    resolve({ ok: true, ...result });
                },
                onError: (error) => {
                    cleanup();
                    resolve({ ok: false, error });
                },
                onCancel: () => {
                    cleanup();
                    resolve({ ok: false, cancelled: true });
                },
            });

            if (widget) {
                widget.show();
            } else {
                cleanup();
                reject(new Error('DexasignVerify: Failed to initialise widget'));
            }
        });
    }

    /**
     * Reset all widgets
     */
    reset() {
        this.modals.forEach(modal => modal.destroy());
        this.modals.clear();
        this.autoInitialized = false;
    }
}

// Create global instance
const DexasignVerify = new DexasignVerifyWidget();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => DexasignVerify.autoInit());
} else {
    // DOM already loaded
    DexasignVerify.autoInit();
}

// Expose global API
window.DexasignVerify = DexasignVerify;

// window.Dexasign is a convenience alias — allows simpler imperative usage:
//   const result = await window.Dexasign.verifyPresence({ applicationId: '...' });
window.Dexasign = DexasignVerify;

export default DexasignVerify;
